<template>
  <div ref="wrapper" class="wrapper" v-resize="onResize">
    <ECharts ref="gauge" class="chart" :options="gaugeOptions" autoresize />
    <ECharts ref="heartbeat" class="chart" :options="heartbeatOptions" autoresize />
  </div>
</template>

<script>
import { merge, pick } from 'lodash';
import ECharts from 'vue-echarts/components/ECharts.vue';
import 'echarts/lib/chart/bar';
import chartable from '@/mixins/chartable';
import initial from '@/components/chart/options/initial.js';
import dark from '@/components/chart/options/dark.js';
import light from '@/components/chart/options/light.js';

export default {
  mixins: [chartable],
  components: {
    ECharts,
  },
  props: {
    // system
    system: {
      type: Object,
      required: true,
    },
    // 게이지 items
    items: {
      type: Array,
      required: true,
    },
    // 게이지 values
    values: {
      type: Array,
      required: true,
    },
    // 게이지 구성 level 정보
    levels: {
      type: Array,
      required: true,
    },
    // y 좌표 최대값
    yAxisMax: {
      type: Number,
      required: true,
    },
    // graph title
    title: {
      type: Object,
      default() {
        return {};
      },
    },
    // 심박동 font color
    heartbeatFontColor: {
      type: String,
      default: '#6B717D',
    },
    // chart grid
    chartGrid: {
      type: Object,
      default() {
        return {
          top: '25px',
          bottom: '20px',
          left: '5px',
          right: '5px',
        };
      },
    },
    contextmenu: {
      type: Function,
      default: e => e,
    },
  },
  data() {
    return {
      gaugeOptions: {},
      heartbeatOptions: {},
      systemChanged: true,
      hearbeatReady: false,
      runnable: 0,
      activateInterval: 0,
      gauge: {
        onReady: false,
        value: Array(this.items.length).fill(0),
        curr: Array(this.items.length).fill(0),
        list: Array(this.items.length).fill([]),
      },
      heartbeat: {
        onReady: false,
        fontSize: this.heartbeatFontSize,
        positions: Array(this.items.length).fill(null),
      },
      loading: false,
      selecteditem: {},
    };
  },
  computed: {
    isDark() {
      return this.$vuetify.theme.isDark;
    },
    theme() {
      return this.isDark ? dark : light;
    },
  },
  watch: {
    system: {
      immediate: true,
      handler: 'changSystemHandler',
    },
    items: {
      deep: true,
      handler: 'changItemsHandler',
    },
    values: {
      handler: 'changeValuesHandler',
    },
    isDark: {
      handler: 'changColorHandler',
    },
  },
  methods: {
    gaugeInit() {
      const option = {
        animation: false,
        tooltip: {
          show: false,
        },
        xAxis: {
          show: false,
          type: 'value',
          data: [''],
          max: 200,
          min: 0,
        },
        yAxis: {
          show: true,
          max: this.yAxisMax,
          min: 0,
          axisLine: {
            show: false,
          },
        },
        graphic: [this.getLevelLegend()],
        grid: this.chartGrid,
        series: [
          {
            type: 'custom',
            silent: true,
            animation: false,
            encode: {
              x: [0, 1],
              y: 2,
            },
            data: [0, 0, 0, 0],
          },
        ],
      };
      this.$refs.gauge.chart.setOption(merge({}, initial, this.theme, option), true);
    },
    hearbeatInit() {
      const option = {
        animation: false,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: params => this.tooltipFormatter(params),
        },
        xAxis: {
          show: true,
          data: [''],
        },
        yAxis: {
          show: false,
          max: this.yAxisMax,
        },
        grid: this.chartGrid,
        series: [
          {
            type: 'pictorialBar',
            symbol: 'rect',
            data: [''],
          },
        ],
      };
      this.$refs.heartbeat.chart.setOption(merge({}, pick(initial, 'tooltip'), option), true);
    },
    changSystemHandler() {
      this.deactivate();
      this.systemChanged = true;
      this.loading = true;
    },
    changItemsHandler() {
      if (!this.systemChanged) this.changColorHandler();
    },
    changeValuesHandler() {
      this.$nextTick(() => {
        if (this.systemChanged) {
          this.$refs.gauge.chart.setOption(this.getGaugeOptions(), true);
          this.$refs.heartbeat.chart.setOption(this.getHeartbeatOptions(), true);
          this.values.forEach((value, index) => this.setGauge(index, value));
          this.loading = false;
        } else {
          this.values.forEach((value, index) => this.setGauge(index, value));
        }
        this.items.forEach((item, index) => this.setHeartbeat(index));
        this.systemChanged = false;
      });
    },
    changColorHandler() {
      this.items.forEach((item, index) => {
        this.setGaugeSeriesColor(index);
        this.setHeartbeatSymbolColor(index);
      });
    },
    deactivate() {
      this.systemChanged = false;
    },
    xAxis() {
      // const data = this.items.map(item => item.id);
      const data = this.items.map(item => item.shortAlias);
      const max = (data.length + 1) * 100; // 최대 수치
      const interval = max / (data.length * 2); // 표시 간격 수치
      return {
        data,
        max,
        interval,
      };
    },
    yAxis() {
      const interval = Math.floor(this.yAxisMax / 10);
      return {
        interval,
      };
    },
    getGaugeOptions() {
      const xAxis = this.xAxis();
      const option = {
        animation: false,
        tooltip: {
          show: false,
        },
        xAxis: {
          show: false,
          type: 'value',
          data: xAxis.data,
          max: xAxis.max,
          min: 0,
        },
        yAxis: {
          show: true,
          max: this.yAxisMax,
          min: 0,
          axisLine: {
            show: false,
          },
        },
        graphic: [this.getLevelLegend()],
        grid: this.chartGrid,
        series: this.values.map(() => ({
          type: 'custom',
          renderItem: this.renderItem,
          silent: true,
          animation: false,
          encode: {
            x: [0, 1],
            y: 2,
          },
          data: [0, 0, 0, 0],
        })),
      };
      return merge({}, initial, this.theme, option);
    },
    getHeartbeatOptions() {
      const xAxis = this.xAxis();
      const option = {
        animation: false,
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: params => this.tooltipFormatter(params),
        },
        xAxis: {
          show: true,
          data: xAxis.data,
          axisLabel: {
            fontWeight: 'normal',
            fontSize: 9,
            color: '#bbbbaa',
          },
        },
        yAxis: {
          show: false,
          max: this.yAxisMax,
        },
        grid: this.chartGrid,
        series: [
          {
            type: 'pictorialBar',
            symbol: 'rect',
            data: this.items.map((item, index) => this.getHeartBeatSeriesData(index)),
          },
        ],
      };
      return merge({}, pick(initial, 'tooltip'), pick(this.theme, 'xAxis'), option);
    },
    getLevelLegend() {
      const circle = (color, left) => ({
        type: 'circle',
        z: 100,
        left,
        shape: { cx: 5, cy: 5, r: 5 },
        style: { fill: color },
      });
      const text = (color, left, text) => ({
        type: 'text',
        z: 100,
        top: 1,
        left,
        style: {
          fill: color,
          text,
          font: 'bold 11px',
        },
      });
      const left = [
        [5, 20],
        [90, 105],
        [155, 170],
        [220, 235],
        [295, 310],
      ];
      const children = this.levels.reduce((acc, level, index) => {
        acc.push(circle(level.color, left[index][0]));
        acc.push(text(this.chartColor.axisLabel, left[index][1], level.name));
        return acc;
      }, []);
      const legend = {
        type: 'group',
        top: 'top',
        z: 100,
        children,
      };
      return legend;
    },
    getHeartBeatSeriesData(index) {
      return {
        value: 0,
        symbolPosition: 'end',
        animation: false,
        itemStyle: {
          color: '#0c96d3',
        },
        label: {
          show: true,
          position: 'top',
          fontSize: 12,
          color: this.items[index].color,
          formatter: '',
        },
      };
    },
    tooltipFormatter(params) {
      const param = params.find(param => this.items[param.dataIndex].active);
      if (!param) return null;

      const index = param.dataIndex;
      const item = this.items[index];
      this.selecteditem = item;
      let total = 0;
      let list = [];
      // 종류별
      this.gauge.list[index].forEach((gauge, index) => {
        const level = this.levels[index];
        if (gauge) list.push([`<span style="color:${level.color};font-weight:900;margin:0 0 0 10px;">`, `${level.name}`, '</span> : ', gauge].join(''));
        total += gauge;
      });
      // const label = [this.circle(item.color), `<span style="color:${item.color};font-weight:900;">`, item.id, '</span> : ', total].join('');
      const label = [this.circle(item.color), `<span style="color:${item.color};font-weight:900;">`, item.shortAlias, '</span> : ', total].join('');
      list.unshift(label);
      return list.join('<br/>');
    },
    renderItem(params, api) {
      const seriesIndex = api.value(3);
      const yValue = api.value(2);
      const start = api.coord([api.value(0), yValue]);
      const end = api.coord([api.value(1), yValue]);
      const style = api.style();
      const height = (150 / this.yAxisMax) * (this.$refs.gauge.$el.offsetHeight / 600);

      // 심박동 그래프 위치 지정
      this.heartbeat.positions[seriesIndex] = {
        start: start[0],
        height: height,
        width: end[0] - start[0],
      };

      // 수치 그래프 위치 지정
      return {
        type: 'rect',
        shape: {
          x: start[0],
          y: start[1],
          width: end[0] - start[0],
          height: height,
        },
        style: style,
      };
    },
    getGaugeLevelData(heightSum, height, seriesIndex, levelIndex) {
      const xAxis = this.xAxis();
      const interval = xAxis.interval;
      const unit = this.yAxisMax / (this.yAxisMax * 2);
      const list = [];
      const gap = 1 / 4; // gauge 간격 비율 25%
      const center = (seriesIndex * 2 + 1) * interval; // 현재 데이터 기준점
      const start = center - interval * (gap * 3); // 기준점 좌측 -75%
      const finish = center + interval * (gap * 3); // 기준점 우측 +75%
      const startHeight = heightSum * 2;
      const itemActive = this.items[seriesIndex].active;
      Array(height * 2)
        .fill({})
        .forEach((item, index) => {
          if (index % 2) {
            const color = this.levels[levelIndex].color;
            list.push({
              value: [start, finish, (index + startHeight + 1) * unit, seriesIndex, `${this.levels[levelIndex].name}`],
              levelIndex: levelIndex,
              itemStyle: {
                color: color,
                opacity: itemActive ? 1 : 0.4,
              },
            });
          }
        });
      return list;
    },
    setGauge(seriesIndex, rawData) {
      if (!this.$refs.gauge) return;
      let gauge = [];
      let heightSum = 0; // 그래프 높이 값
      let heightSumValue = 0; // 실제 수치 값
      rawData.forEach((height, levelIndex) => {
        heightSumValue += height;
        height = height > 10 ? 10 : height;
        gauge = this.getGaugeLevelData(heightSum, height, seriesIndex, levelIndex).concat(gauge);
        heightSum += height;
      });
      this.gauge.value[seriesIndex] = heightSumValue; // 실제 수치 값
      this.gauge.curr[seriesIndex] = heightSum; // 그래프 높이 값
      this.gauge.list[seriesIndex] = rawData;
      if (!this.$refs.gauge) return;
      const series = this.$refs.gauge.chart.getOption().series;
      series[seriesIndex].data = gauge;
      this.$refs.gauge.chart.setOption({ series });
    },
    setGaugeSeriesColor(seriesIndex) {
      const options = this.$refs.gauge.chart.getOption();
      options.series[seriesIndex].data.forEach((data, index) => {
        const color = this.levels[data.levelIndex].color;
        const itemActive = this.items[seriesIndex].active;
        options.series[seriesIndex].data[index].itemStyle.color = color;
        options.series[seriesIndex].data[index].itemStyle.opacity = itemActive ? 1 : 0.4;
      });
      options.yAxis[0].splitLine.lineStyle.color = this.theme.yAxis.splitLine.lineStyle.color;
      options.yAxis[0].splitArea.areaStyle.color = this.theme.yAxis.splitArea.areaStyle.color;
      this.$refs.gauge.chart.setOption(options);
    },
    setHeartbeat(seriesIndex) {
      if (!this.$refs.heartbeat) return;
      this.setHeartbeatSymbolSize(seriesIndex, false);
      const gaugeCurValue = this.gauge.curr[seriesIndex];
      const series = this.$refs.heartbeat.chart.getOption().series;
      series[0].data[seriesIndex].value = gaugeCurValue;
      series[0].data[seriesIndex].label.formatter = `${this.gauge.value[seriesIndex]}`;
      const animation = false;
      this.$refs.heartbeat.chart.setOption({ animation, series });
      this.setHeartbeatSymbolColor(seriesIndex);
    },
    setHeartbeatSymbolSize(seriesIndex, animation) {
      const series = this.$refs.heartbeat.chart.getOption().series;
      series[0].symbolSize = [0, 0];
      this.$refs.heartbeat.chart.setOption({ animation, series });
    },
    setHeartbeatSymbolColor(seriesIndex) {
      const option = this.$refs.heartbeat.chart.getOption();
      option.series[0].data[seriesIndex].label.color = this.items[seriesIndex].active ? this.items[seriesIndex].color : this.getThemeColor(this.items[seriesIndex].color, 20, 45); // instace 지정 색 표시
      this.$refs.heartbeat.chart.setOption(option);
    },
    onResize() {
      this.$refs.heartbeat.chart.setOption({ animation: false });
      const wrapper = this.$refs.wrapper;
      this.$refs.gauge.$el.style.height = `${wrapper.offsetHeight}px`;
      this.$refs.gauge.$el.style.width = `${wrapper.offsetWidth}px`;
      this.$refs.heartbeat.$el.style.height = `${wrapper.offsetHeight}px`;
      this.$refs.heartbeat.$el.style.width = `${wrapper.offsetWidth}px`;
      if (this.hearbeatReady) this.items.forEach((item, seriesIndex) => this.setHeartbeatSymbolSize(seriesIndex, false));
    },
    addEventChart(target) {
      this.$refs[target].chart.on('finished', () => {
        this.$refs[target].chart.off('finished');
        const canvas = this.$refs[target].chart.getDom().getElementsByTagName('canvas')[0];
        canvas.addEventListener('contextmenu', event => this.contextmenu(event, this.selecteditem));
      });
    },
  },
  created() {
    this.$nextTick(() => {
      this.gaugeInit();
      this.hearbeatInit();
      this.addEventChart('gauge');
      this.addEventChart('heartbeat');
    });
  },
  beforeDestroy() {
    this.deactivate();
  },
};
</script>
<style scoped>
.wrapper {
  position: relative;
  height: 100%;
  width: 100%;
}
.chart {
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 0;
}
</style>
